import { Project } from 'mdello-client-ts/dist/src/interfaces/common/project.interface';
import { useMutation, useQueryClient } from 'react-query';
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum';
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage';
import { useNotification } from 'src/providers/notificationsProvider';
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum';
import { reorderSingleContent } from 'src/services/napi/Contents/reorderSingleContent';
import {
  ReorderSingleContentRequestBody,
  ReorderSingleContentResponseData
} from 'src/services/napi/Contents/reorderSingleContent/interfaces/reorderSingleContent.interface';
import { UseReorderSingleContentParams } from './interfaces/useReorderSingleContent.interface';

const reorderSingleContentMutationFn = async (
  body: ReorderSingleContentRequestBody
): Promise<ReorderSingleContentResponseData> => {
  const currentProject = getDataFromLocalStorage<Project>(LocalStorageKey.CURRENT_PROJECT);

  if (!currentProject?._id) {
    throw new Error('Project ID is missing');
  }

  const response = await reorderSingleContent({ body, projectId: currentProject._id });

  if (!response.success) {
    throw new Error('Failed to reorder content');
  }

  return response;
};

export const useReorderSingleContent = (params: UseReorderSingleContentParams) => {
  const { onError, onSuccess } = params || {};
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  return useMutation(
    (data: ReorderSingleContentRequestBody) => reorderSingleContentMutationFn(data),
    {
      onError: (error, variables) => {
        notify({ message: 'Error reordering content', severity: 'error' });
        onError?.(error, variables);
      },
      onSuccess: async (data, variables) => {
        await queryClient.invalidateQueries([QueryKey.CONTENTS]);
        onSuccess?.(data, variables);
      }
    }
  );
};
